import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  User, 
  Clock, 
  Instagram, 
  Link as LinkIcon,
  Save,
  Mail,
  Crown,
  Sparkles,
  Video,
  Eye,
  EyeOff,
  Gift,
  ChevronRight,
  RefreshCw,
  Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SettingsPage = () => {
  const { user, refreshUser, marketingMode, toggleMarketingMode } = useAuth();
  const [preferredTime, setPreferredTime] = useState(user?.schedule_preferred_time || "09:00");
  const [dailyEmail, setDailyEmail] = useState(!!user?.daily_email_enabled);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDailyEmail(!!user?.daily_email_enabled);
    if (user?.schedule_preferred_time) setPreferredTime(user.schedule_preferred_time);
  }, [user?.daily_email_enabled, user?.schedule_preferred_time]);

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/schedule/settings`, {
        preferred_time: preferredTime,
        daily_email_enabled: dailyEmail,
      });
      toast.success("Email reminder settings saved");
      refreshUser();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight">
          Settings
        </h1>
        <p className="text-ink-500 mt-1">Manage your account and preferences</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Referral Section - Add a friend banner */}
        <Link 
          to="/dashboard/tokens"
          className="block p-6 rounded-2xl bg-gradient-to-r from-amber-900/40 to-orange-900/40 border border-amber-500/30 hover:border-amber-400/50 transition-all duration-300 group"
          data-testid="referral-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Gift className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-ink-950 group-hover:text-amber-300 transition-colors">
                  Add a Friend & Both Get Tokens!
                </h2>
                <p className="text-sm text-amber-300/80">
                  You get 50 tokens, they get 20 bonus tokens
                </p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-amber-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        {/* Profile Section */}
        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-coral-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-coral-500" />
            </div>
            <h2 className="text-lg font-semibold text-ink-950">Profile</h2>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-ink-500">Name</Label>
              <Input
                value={user?.name || ""}
                disabled
                className="bg-ink-100 border-ink-200 text-ink-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-ink-500">Email</Label>
              <Input
                value={user?.email || ""}
                disabled
                className="bg-ink-100 border-ink-200 text-ink-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-ink-500">Subscription</Label>
              <div className="p-3 rounded-lg bg-ink-100 border border-ink-200">
                <span className="text-ink-950 font-medium capitalize">{user?.subscription_tier} Plan</span>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Email Reminder */}
        <div
          className="p-6 rounded-2xl bg-white border border-ink-200"
          data-testid="daily-email-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-coral-500/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-coral-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink-950">Daily Email Reminder</h2>
              <p className="text-xs text-ink-500">
                We&apos;ll send you a personalised XEA summary at the time you pick above.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-ink-50 border border-ink-200">
            <div className="min-w-0">
              <p className="text-ink-950 font-medium">
                {dailyEmail ? "Daily email is ON" : "Daily email is OFF"}
              </p>
              <p className="text-xs text-ink-500 mt-0.5">
                {dailyEmail
                  ? `You&rsquo;ll get a digest around ${preferredTime} (Cyprus time) with new tips, community engagement, and your token snapshot.`
                  : "Turn it on to get a once-a-day nudge at your chosen time."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={dailyEmail}
              onClick={() => setDailyEmail((v) => !v)}
              data-testid="daily-email-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral-500/50 ${
                dailyEmail ? "bg-coral-500" : "bg-ink-300"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  dailyEmail ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          <p className="text-[11px] text-ink-500 mt-3 leading-relaxed">
            Push notifications have been retired. You&apos;ll only receive the
            digest if this toggle is ON. Transactional emails (receipts, password
            resets, tip notifications) are always sent so we can keep your
            account safe.
          </p>
        </div>

        {/* Connected Platforms */}
        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-coral-500/20 flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-coral-500" />
            </div>
            <h2 className="text-lg font-semibold text-ink-950">Connected Platforms</h2>
          </div>

          <div className="space-y-4">
            {/* Instagram */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-ink-100 border border-ink-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-ink-950" />
                </div>
                <div>
                  <p className="text-ink-950 font-medium">Instagram</p>
                  <p className="text-xs text-ink-500">Not connected</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-ink-300 text-ink-700 hover:bg-ink-200"
                data-testid="connect-instagram-btn"
              >
                Connect
              </Button>
            </div>

            {/* TikTok */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-ink-100 border border-ink-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-ink-50 flex items-center justify-center">
                  <TikTokIcon className="w-5 h-5 text-ink-950" />
                </div>
                <div>
                  <p className="text-ink-950 font-medium">TikTok</p>
                  <p className="text-xs text-ink-500">Not connected</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-ink-300 text-ink-700 hover:bg-ink-200"
                data-testid="connect-tiktok-btn"
              >
                Connect
              </Button>
            </div>

            <p className="text-xs text-ink-500">
              Connect your social media accounts to enable automatic posting of approved content.
            </p>
          </div>
        </div>

        {/* Daily Report Section - Admin Only */}
        {user?.is_admin && (
          <div className="p-6 rounded-2xl bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-coral-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Clock className="w-5 h-5 text-ink-950" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-ink-950">Daily Report</h2>
                <p className="text-xs text-ink-700">Automated email at 00:00 Cyprus time</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-white/60 border border-ink-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-ink-950 font-medium">Scheduled Report</p>
                    <p className="text-xs text-ink-500 mt-1">
                      Daily subscription summary sent to: <span className="text-coral-500">aliibo99998@icloud.com</span>
                    </p>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-coral-500/20 border border-coral-500/30">
                    <span className="text-coral-500 text-xs font-medium">Active</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/send-test-report`);
                    toast.success("Test report sent to your iCloud email!");
                  } catch (error) {
                    toast.error("Failed to send test report");
                  }
                }}
                variant="outline"
                className="w-full border-coral-500/40 text-coral-500 hover:bg-coral-500/10"
                data-testid="send-test-report-btn"
              >
                📧 Send Test Report Now
              </Button>

              <p className="text-xs text-ink-500">
                Reports include: Total subscriptions per tier, yesterday's new signups, and daily revenue.
              </p>
            </div>
          </div>
        )}

        {/* App Cache — useful after a redeploy when the PWA service worker
            is still serving stale assets (white screens, missing icons, old
            copy). Available to every user. */}
        <div
          className="p-6 rounded-2xl bg-white/80 border border-ink-200"
          data-testid="app-cache-section"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink-950">App Cache</h2>
              <p className="text-xs text-ink-500">
                Stuck on an old version after we shipped an update? Clear the cache to force a fresh download.
              </p>
            </div>
          </div>

          <Button
            onClick={async () => {
              try {
                // 1. Unregister every service worker (the PWA shell)
                if ("serviceWorker" in navigator) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map((r) => r.unregister()));
                }
                // 2. Wipe every Cache Storage bucket (offline assets)
                if ("caches" in window) {
                  const keys = await caches.keys();
                  await Promise.all(keys.map((k) => caches.delete(k)));
                }
                // 3. Clear non-auth localStorage entries (keep the token so the user stays signed in)
                const preserved = {
                  token: localStorage.getItem("token"),
                  auth_token: localStorage.getItem("auth_token"),
                };
                localStorage.clear();
                Object.entries(preserved).forEach(([k, v]) => {
                  if (v) localStorage.setItem(k, v);
                });
                // 4. Drop any sessionStorage state too
                try { sessionStorage.clear(); } catch (_) { /* ignore quota errors */ }

                toast.success("App cache cleared. Reloading…");
                // Bypass the http cache on the way back
                setTimeout(() => window.location.reload(true), 600);
              } catch (e) {
                console.error("Cache clear failed:", e);
                toast.error("Could not clear cache. Try a hard refresh (Cmd/Ctrl-Shift-R).");
              }
            }}
            variant="outline"
            className="w-full border-amber-500/40 text-amber-700 hover:bg-amber-500/10"
            data-testid="clear-app-cache-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear app cache & reload
          </Button>

          <p className="text-xs text-ink-500 mt-2">
            Keeps you signed in. Forces every page to redownload the latest version from XEA.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
